import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getTenants, type Tenant } from '@/services'
import { AppButton, EmptyState, KPICard, PlanBadge, Skeleton } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'

interface MetricDef {
  id: string
  label: string
  icon: string
  get: (t: Tenant) => number
}

const METRICS: MetricDef[] = [
  { id: 'orders', label: 'Pedidos (mes)', icon: 'shoppingBag', get: (t) => t.current_orders_month || 0 },
  { id: 'products', label: 'Productos', icon: 'package', get: (t) => t.current_products_count || 0 },
  { id: 'storage', label: 'Storage (MB)', icon: 'hardDrive', get: (t) => t.current_storage_mb || 0 },
]

const fmt = (v: number) => v.toLocaleString('es-AR')

export function StatsPage() {
  const [metricId, setMetricId] = useState('orders')
  const metric = METRICS.find((m) => m.id === metricId) || METRICS[0]

  const { data: result, isLoading } = useQuery({
    queryKey: ['tenants', 'stats'],
    queryFn: () => getTenants({ pageSize: 200 }),
  })

  const tenants = result?.data || []
  const ranked = [...tenants].sort((a, b) => metric.get(b) - metric.get(a)).slice(0, 15)
  const maxVal = ranked[0] ? metric.get(ranked[0]) : 1
  const total = tenants.reduce((sum, t) => sum + metric.get(t), 0)
  const avg = tenants.length ? Math.round(total / tenants.length) : 0
  const top = ranked[0]

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Plataforma</div>
          <h1>Stats avanzadas</h1>
          <div className="subtitle">Ranking cross-tenant por métrica — útil para detectar power users y outliers.</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="download">Exportar</AppButton>
        </div>
      </div>

      <div className="row gap-2" style={{ marginBottom: 18, flexWrap: 'wrap' }}>
        {METRICS.map((m) => (
          <button key={m.id} className={`filter-chip ${metricId === m.id ? 'active' : ''}`} onClick={() => setMetricId(m.id)} type="button">
            <Icon name={m.icon} size={12} />{m.label}
          </button>
        ))}
      </div>

      <>
        <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 18 }}>
          <KPICard label={`Total ${metric.label}`} icon={metric.icon} value={fmt(total)} loading={isLoading} feature />
          <KPICard label="Promedio por tenant" value={fmt(avg)} loading={isLoading} />
          <KPICard label="Top tenant" value={top ? top.business_name.slice(0, 18) + (top.business_name.length > 18 ? '...' : '') : '-'} trend={top ? fmt(metric.get(top)) : undefined} loading={isLoading} />
          <KPICard label="Tenants midiendo" value={tenants.filter((t) => metric.get(t) > 0).length} loading={isLoading} />
        </div>

        <div className="card">
          <div className="card-header"><h3>Ranking — {metric.label}</h3></div>
          <div className="card-body flush">
            {isLoading ? (
              <div style={{ padding: 16 }}><Skeleton height={260} /></div>
            ) : ranked.length === 0 ? (
              <EmptyState icon="trending" title="Sin datos para esta métrica" desc="Cuando los tenants reporten uso, el ranking aparece acá." />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th>Tenant</th>
                    <th>Plan</th>
                    <th style={{ width: 280 }}>{metric.label}</th>
                    <th style={{ textAlign: 'right' }}>Valor</th>
                    <th style={{ textAlign: 'right' }}>% del total</th>
                  </tr>
                </thead>
                <tbody>
                  {ranked.map((t, i) => {
                    const val = metric.get(t)
                    const pct = maxVal > 0 ? (val / maxVal) * 100 : 0
                    const totalPct = total > 0 ? (val / total) * 100 : 0
                    return (
                      <tr key={t.id}>
                        <td><span style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 16, color: i < 3 ? 'var(--bisne-green-800)' : 'var(--text-3)' }}>{i + 1}</span></td>
                        <td>
                          <div className="cell-primary">{t.business_name}</div>
                          <div className="cell-secondary"><span className="code-chip">{t.slug}</span></div>
                        </td>
                        <td><PlanBadge plan={t.plan_slug || t.plan_name} /></td>
                        <td>
                          <div style={{ background: 'var(--surface-3)', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: 'var(--bisne-green)', borderRadius: 4 }} />
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}><span className="tabular cell-primary">{fmt(val)}</span></td>
                        <td style={{ textAlign: 'right' }}><span className="tabular" style={{ fontSize: 12, color: 'var(--text-3)' }}>{totalPct.toFixed(1)}%</span></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </>
    </>
  )
}
